import React, { useState, useEffect, useRef, useMemo } from "react";
import JoditEditor from "jodit-react";
import { toast } from "react-toastify";
import { useMyContext } from "../../components/Provider/CommonProvider";
import AdminSaveHeader from "../../components/CommonComponents/AdminSaveHeader";
import MessageLabel from "../../components/CommonComponents/MessageLabel";
import FormContainer from "../../components/CommonComponents/FormContainer";
import Wrapper from "../../components/CommonComponents/Wrapper";
import MessageHeading from "../../components/CommonComponents/MessageHeading";
import AdminCustomInput from "../../components/CommonComponents/AdminCustomInput";
import AdminCustomButton from "../../components/CommonComponents/AdminCustomButton";
import AdminImageUploadZone from "../../components/CommonComponents/AdminImageUploadZone";
import Select from "react-select";
import { TagsInput } from "react-tag-input-component";
import Defaultloadinggif from "../../layout/Others/Defaultloadinggif";
import Response_Processing from "../../layout/Others/Response_Processing";

export default function DonationDetails() {
    const { fetchRequest, globalImageUplaodAPI } = useMyContext();
    const baseURL = process.env.REACT_APP_Baseurl;
    const apiGetSettings = `${baseURL}/admin/main-settings/get`;
    const apiUpdateSettings = `${baseURL}/admin/main-settings/update`;
    const apiPaymentSettings = `${baseURL}/admin/payment-settings/index`;

    const [loading, setLoading] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [settingsId, setSettingsId] = useState(null);
    const [qrCodeFile, setQrCodeFile] = useState(null);
    const [qrCodePreview, setQrCodePreview] = useState(null);

    const [formData, setFormData] = useState({
        donation_title: "",
        donation_description: "",
        donation_cta_label: "Donate",
        donation_mobile: "",
        donation_address: "",
        donation_qr_code: "",
        donation_payment_type: [], // Array of selected payment methods (stripe, donorbox, etc.)
        donation_donor_box_url: "",
        donation_default_amount: [] // Array of amounts
    });

    const [availableGateways, setAvailableGateways] = useState([]);
    const fileInputRef = useRef(null);

    const joditConfig = useMemo(() => ({
        readonly: false,
        placeholder: 'Enter a brief description...',
        height: 300,
        maxHeight: 500,
        autobheight: false,
        toolbarSticky: true,
        width: '100%',
        theme: 'dark', // Match the admin theme
    }), []);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            // Fetch Payment Settings to know available gateways
            const paymentResponse = await fetchRequest({
                method: 'GET',
                url: apiPaymentSettings,
            });

            const gatewayOptions = [];
            if (paymentResponse?.status && paymentResponse?.data) {
                const data = paymentResponse.data;
                Object.keys(data).forEach(key => {
                    if (data[key] === 1 || data[key] === "1") {
                        const label = key.charAt(0).toUpperCase() + key.slice(1);
                        gatewayOptions.push({ value: key, label: label });
                    }
                });
            }
            // Add Donorbox as a method
            gatewayOptions.push({ value: 'donorbox', label: 'Donor Box' });
            setAvailableGateways(gatewayOptions);

            // Fetch Admin Settings
            const settingsResponse = await fetchRequest({
                method: 'GET',
                url: apiGetSettings,
                params: {
                    fields: 'donation_title,donation_description,donation_cta_label,donation_mobile,donation_address,donation_qr_code,donation_payment_type,donation_donor_box_url,donation_default_amount'
                }
            });

            if (settingsResponse?.status && settingsResponse?.data) {
                const data = settingsResponse.data;
                setSettingsId(data.id);

                // Parse default amounts
                let parsedAmounts = [];
                if (data.donation_default_amount) {
                    try {
                        parsedAmounts = JSON.parse(data.donation_default_amount);
                    } catch (e) {
                        try {
                            parsedAmounts = data.donation_default_amount.split(',');
                        } catch (innerError) {
                            parsedAmounts = ["10", "20", "50", "100"];
                        }
                    }
                } else {
                    parsedAmounts = ["10", "20", "50", "100"];
                }

                // Parse Payment Types
                let selectedTypes = [];
                try {
                    const parsedTypes = data.donation_payment_type ? JSON.parse(data.donation_payment_type) : [];
                    selectedTypes = Array.isArray(parsedTypes) ? parsedTypes.map(type => ({
                        value: type,
                        label: type.charAt(0).toUpperCase() + type.slice(1)
                    })) : [];
                } catch (e) {
                    console.error("Error parsing donation_payment_type", e);
                }

                setFormData({
                    donation_title: data.donation_title || "",
                    donation_description: data.donation_description || "",
                    donation_cta_label: data.donation_cta_label || "Donate now",
                    donation_mobile: data.donation_mobile || "",
                    donation_address: data.donation_address || "",
                    donation_qr_code: data.donation_qr_code || "",
                    donation_payment_type: selectedTypes,
                    donation_donor_box_url: data.donation_donor_box_url || "",
                    donation_default_amount: parsedAmounts,
                });

                if (data.donation_qr_code) {
                    setQrCodePreview(data.donation_qr_code);
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePaymentTypeChange = (selectedOptions) => {
        setFormData(prev => ({ ...prev, donation_payment_type: selectedOptions || [] }));
    };

    const handleAmountsChange = (newAmounts) => {
        setFormData(prev => ({ ...prev, donation_default_amount: newAmounts }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                toast.error("Invalid file type. Please upload an image.");
                return;
            }
            setQrCodeFile(file);
            setQrCodePreview(URL.createObjectURL(file));
        }
    };

    const handleDeleteImage = () => {
        setQrCodeFile(null);
        setQrCodePreview(null);
        setFormData(prev => ({ ...prev, donation_qr_code: "" }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSave = async () => {
        setShowOverlay(true);
        try {
            const textData = {
                donation_title: formData.donation_title,
                donation_description: formData.donation_description,
                donation_cta_label: formData.donation_cta_label,
                donation_mobile: formData.donation_mobile,
                donation_address: formData.donation_address,
                donation_payment_type: JSON.stringify(formData.donation_payment_type.map(opt => opt.value)),
                donation_donor_box_url: formData.donation_donor_box_url,
                donation_default_amount: JSON.stringify(formData.donation_default_amount),
            };

            const updateResponse = await fetchRequest({
                method: 'POST',
                url: apiUpdateSettings,
                data: textData
            });

            if (!updateResponse?.status) {
                throw new Error(updateResponse?.message || "Failed to save settings");
            }

            if (qrCodeFile && settingsId) {
                const imageFormData = new FormData();
                imageFormData.append('modelName', 'AdminSetting');
                imageFormData.append('id', settingsId);
                imageFormData.append('imageFields[]', 'donation_qr_code');
                imageFormData.append('donation_qr_code', qrCodeFile);

                const uploadResponse = await fetchRequest({
                    method: 'POST',
                    url: globalImageUplaodAPI(),
                    data: imageFormData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (!uploadResponse?.status) {
                    toast.warning("Settings saved but image upload failed: " + uploadResponse?.message);
                }
            }

            toast.success("Donation details saved successfully");
            fetchInitialData();

        } catch (error) {
            console.error(error);
            toast.error(error?.message || "Something went wrong saving settings");
        } finally {
            setShowOverlay(false);
        }
    };

    if (loading) return <Defaultloadinggif className="heightOnLoader" />;

    return (
        <div className="m-0 p-0">
            {showOverlay && <Response_Processing />}
            <AdminSaveHeader
                heading="Donation Details"
                saveText="Save Settings"
                saveMethod={handleSave}
                loading={showOverlay}
            />

            <FormContainer>
                <Wrapper className="w-100">
                    <MessageHeading text="Page Content" />
                    <div className="row mb-3">
                        <div className="col-md-6 mb-3">
                            <MessageLabel text="Donation Title" />
                            <AdminCustomInput
                                name="donation_title"
                                value={formData.donation_title}
                                onChange={handleChange}
                                placeholder="e.g. Support Our Mission"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <MessageLabel text="Donation Description" />
                            <JoditEditor
                                value={formData.donation_description}
                                config={joditConfig}
                                onBlur={(newContent) => setFormData(prev => ({ ...prev, donation_description: newContent }))}
                                onChange={(newContent) => { }} // Required by JoditEditor
                            />
                        </div>
                    </div>

                    <MessageHeading text="General Information" />

                    <div className="row mb-3">
                        <div className="col-md-6 mb-3">
                            <MessageLabel text="Payment Methods" />
                            <Select
                                options={availableGateways}
                                value={formData.donation_payment_type}
                                onChange={handlePaymentTypeChange}
                                isMulti={true}
                                className="mb-3"
                                classNamePrefix="react-select"
                                placeholder="Select Payment Methods"
                            />

                            {formData.donation_payment_type.some(opt => opt.value === 'donorbox') && (
                                <>
                                    <MessageLabel text="Donor Box URL/ID" />
                                    <AdminCustomInput
                                        type="text"
                                        value={formData.donation_donor_box_url}
                                        name="donation_donor_box_url"
                                        onChange={(e) => setFormData(prev => ({ ...prev, donation_donor_box_url: e.target.value }))}
                                        placeholder="Enter Donorbox ID or Campaign URL"
                                    />
                                </>
                            )}
                        </div>
                        <div className="col-md-6 mb-3">
                            <MessageLabel text="CTA Label" />
                            <AdminCustomInput
                                name="donation_cta_label"
                                value={formData.donation_cta_label}
                                onChange={handleChange}
                                placeholder="e.g. Donate Now"
                            />
                            <MessageLabel text="Contact Mobile" />
                            <AdminCustomInput
                                name="donation_mobile"
                                value={formData.donation_mobile}
                                onChange={handleChange}
                                placeholder="Enter mobile number"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6 mb-3">
                            <MessageLabel text="Donation Address" />
                            <textarea
                                className="form-control theme-bg-color text-color border-secondary"
                                name="donation_address"
                                rows="3"
                                value={formData.donation_address}
                                onChange={handleChange}
                                style={{ backgroundColor: 'transparent', borderColor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}
                                placeholder="Enter physical address or other contact info"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <MessageLabel text="Donation Amounts" />
                            <div className="theme-bg-color rounded">
                                <TagsInput
                                    value={formData.donation_default_amount}
                                    onChange={handleAmountsChange}
                                    name="donation_default_amount"
                                    placeHolder="Add amount & press enter"
                                    classNames={{
                                        tag: "theme-bg-color text-color border border-secondary",
                                        input: "theme-bg-color text-color"
                                    }}
                                />
                            </div>
                            <small className="text-muted">Press enter to add an amount.</small>
                        </div>
                    </div>

                    <MessageHeading text="QR Code" />
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <MessageLabel text="Upload QR Code" />
                            <AdminImageUploadZone
                                id="qr-code-upload"
                                name="donation_qr_code"
                                onChange={handleImageChange}
                                selectedFile={qrCodeFile}
                                uploadedImage={!qrCodeFile ? qrCodePreview : null}
                                onDelete={handleDeleteImage}
                                accept="image/*"
                                ref={fileInputRef}
                            />
                        </div>
                    </div>
                </Wrapper>
            </FormContainer>
        </div>
    );
}
